import React, { lazy, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Divider, message, Row, Space, theme } from "antd";
import ScaleModalOp from "./ScaleModalOp";
import ScalePopup from "./ScalePopup";
import LoadingIndicator from "../../../Loaders/LoadingIndicator";
import useQuery from "../../../../hooks/useQuery";
import { opConfirmModal } from "../opConfirmModal";
import { scaleLabels, opProgressScalesDisplayOrder } from "./scaleOptions";
import {
  getOpProgressScalesCountsAPI,
  getOpProgressScalesDataAPI,
  createOpScaleAPI,
  updateOpScaleAPI,
} from "../../../../services/opScale.services";
import {
  hasMocaAssessmentInput,
  isMocaScaleKey,
} from "../../../../helpers/opMocaValidation";
import {
  hasAsiaAssessmentInput,
  isAsiaScaleKey,
} from "../../../../helpers/opAsiaValidation";
import {
  hasVcgAssessmentInput,
  isVcgScaleKey,
} from "../../../../helpers/opVcgValidation";
import {
  hasGcsAssessmentInput,
  isGcsScaleKey,
} from "../../../../helpers/opGcsValidation";
import {
  hasRassAssessmentInput,
  isRassScaleKey,
} from "../../../../helpers/opRassValidation";
import {
  hasNihssAssessmentInput,
  isNihssScaleKey,
} from "../../../../helpers/opNihssValidation";
import {
  hasMasAssessmentInput,
  isMasScaleKey,
} from "../../../../helpers/opMasValidation";
import {
  hasRomAssessmentInput,
  isRomScaleKey,
} from "../../../../helpers/opRomValidation";
import {
  hasCtAssessmentInput,
  isCtScaleKey,
} from "../../../../helpers/opCtValidation";
import {
  hasFgtAssessmentInput,
  isFgtScaleKey,
} from "../../../../helpers/opFgtValidation";
import {
  hasTcmsAssessmentInput,
  isTcmsScaleKey,
} from "../../../../helpers/opTcmsValidation";
import {
  hasBarthelAssessmentInput,
  isBarthelScaleKey,
} from "../../../../helpers/opBarthelValidation";
import {
  hasRpeAssessmentInput,
  isRpeScaleKey,
} from "../../../../helpers/opRpeValidation";
import "./progressScalesOp.css";
import { UiButton, UiSectionCard } from "../../../ui";
const PhysioScalesOp = lazy(() =>
  import(
    "../../../IpEMR/InspectionForms/Examination/PhysioScales/PhysioScalesOp"
  )
);
const convertToArray = (data) => {
  return Object.entries(data).map(([key, value]) => ({
    name: key,
    ...value,
  }));
};

function getRecordsByName(data, name) {
  const item = data.find((entry) => entry.name === name);
  return item ? item.records : [];
}

const getScaleKey = (value) => {
  return Object.keys(scaleLabels).find((key) => scaleLabels[key] === value);
};

const ProgressScalesOp = ({ patientData }) => {
  const { token } = theme.useToken();
  // const admissionId = patientData?.admission_id;
  const params = useQuery();
  const admissionId = params?.aid;
  const [scalesData, setScalesData] = useState([]);
  const [selectedScale, setSelectedScale] = useState(null);
  const [selectedScaleData, setSelectedScaleData] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isStartScalesVisible, setIsStartScalesVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scaleLoading, setScaleLoading] = useState(false);
  const [hasEmr, setHasEmr] = useState(false);
  // console.log(window.location.hash.split("#").pop());
  const [scalesId, setScalesId] = useState(
    window.location.hash.split("#").pop() || null
  );
  // console.log(scalesId);
  const [scaleTab, setScaleTab] = useState("1");
  // console.log(scaleTab);
  const [activeTab, setActiveTab] = useState(null);
  const [visible, setVisible] = useState(false);
  const [activeScaleKey, setActiveScaleKey] = useState(null);
  const [editScaleData, setEditScaleData] = useState(null);
  const [activeModalModel, setActiveModalModel] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [returnToTable, setReturnToTable] = useState(false);
  const [isScalePopupOpen, setIsScalePopupOpen] = useState(false);
  const [isScaleDirty, setIsScaleDirty] = useState(false);
  const scaleRef = useRef(null);

  const forceCloseScalePopup = () => {
    setIsScaleDirty(false);
    setIsScalePopupOpen(false);
    setEditScaleData(null);
    setActiveScaleKey(null);
  };

  const requestCloseScalePopup = () => {
    if (!isScaleDirty) {
      forceCloseScalePopup();
      return;
    }

    opConfirmModal({
      title: "Unsaved changes",
      content:
        "You have unsaved changes in this scale. Do you want to save before closing?",
      okText: "Save",
      cancelText: "Close without saving",
      onOk: async () => {
        const saved = await scaleRef.current?.saveScale?.();
        if (!saved) {
          message.error("Nothing to save. Please fill at least one value.");
          return Promise.reject();
        }
        forceCloseScalePopup();
      },
      onCancel: () => {
        forceCloseScalePopup();
      },
    });
  };

  const modelToFrontendKeyMap = {
    MMT: "mmt",
    ROM: "rom",
    RPE: "rpe",
    SixMinuteWalkTest: "six_min_walk",
    PeakFlowMeterTest: "peak_flow_meter_test",
    GCS: "gcs",
    RASS: "ras_scale",
    MAS: "mas",
    VCG: "vcg",
    MOCA: "moca",
    ASIA: "asia",
    NIHSS: "nihss",
    CoordinationTest: "coordination",
    FingerGripTest: "finger_grip_test",
    TrunkControlTest: "tcms",
    BarthelIndex: "barthel",
  };
  const handleAddOrEditScale = (scaleKey, editData = null) => {
    setActiveScaleKey(scaleKey);
    setEditScaleData(editData);
    setIsScaleDirty(false);
    if (!editData) {
      setScalesId(null);
      window.location.hash = "";
    }
    setIsScalePopupOpen(true);
  };

  const generateEmptyScales = () => {
    return Object.keys(scaleLabels).map((key) => ({
      name: key,
      count: 0,
      records: [],
    }));
  };

  const getScalesData = async () => {
    setLoading(true);
    try {
      const res = await getOpProgressScalesCountsAPI(admissionId);
      const normalized = res.data.data.map((item) => {
        const apiKey = item.key;
        const frontendKey = Object.keys(scaleLabels).find(
          (k) => scaleLabels[k] === apiKey
        );

        return {
          name: frontendKey,
          displayKey: apiKey,
          count: item.count,
        };
      });

      const formatted = res.data.data
        .filter((item) => item.key)
        .map((item) => ({
          name: modelToFrontendKeyMap[item.key],
          displayKey: item.key,
          count: item.count,
        }));

      setScalesData(formatted);
    } catch (e) {
      message.error("Failed to load progress scales");
    } finally {
      setLoading(false);
    }
  };

  const handleBtnClick = (model) => {
    setActiveModalModel(model);

    getOpProgressScalesDataAPI(admissionId, model).then((res) => {
      const normalizedData = res.data.data.map((item) => {
        const dataKey = modelToFrontendKeyMap[model];

        return {
          id: item.id,
          created_at: item.date,
          type: item.type,
          data: item?.[dataKey] ?? item?.data ?? item,
        };
      });

      setSelectedScale(model);
      setSelectedScaleData(normalizedData);
      setIsModalVisible(true);
    });
  };

  const handleCloseModal = () => setIsModalVisible(false);

  const handleStartScalesClick = () => {
    if (!hasEmr) {
      message.error("Please complete EMR before starting scales");
      return;
    }
    setIsStartScalesVisible(true);
  };

  const handleCloseStartScales = () => {
    setIsStartScalesVisible(false);
    setIsEditMode(false);
    setEditScaleData(null);
    window.location.hash = "";
    setScalesId(null);
    setScaleTab("1");
  };

  const frontendToModelMap = Object.entries(modelToFrontendKeyMap).reduce(
    (acc, [model, key]) => {
      acc[key] = model;
      return acc;
    },
    {}
  );

  const handleScalesSubmit = async (scaleKey, data) => {
    if (scaleLoading) return false;
    if (!scaleKey || !data || typeof data !== "object") return false;
    const isMMT = scaleKey === "MMT" || scaleKey === "mmt";

    let hasValidData;

    if (isMMT) {
      hasValidData = ["upper_left_limb", "lower_left_limb", "upper_right_limb", "lower_right_limb", "comments"].some(
        (k) => {
          const v = data?.[k];
          if (v === null || v === undefined) return false;
          if (typeof v === "string") return v.trim() !== "";
          // Allow numeric selections including 0 (some MMT grading scales include 0).
          if (typeof v === "number") return Number.isFinite(v);
          return false;
        }
      );
    } else if (isMocaScaleKey(scaleKey)) {
      hasValidData = hasMocaAssessmentInput(data);
    } else if (isGcsScaleKey(scaleKey)) {
      hasValidData = hasGcsAssessmentInput(data);
    } else if (isRassScaleKey(scaleKey)) {
      hasValidData = hasRassAssessmentInput(data);
    } else if (isNihssScaleKey(scaleKey)) {
      hasValidData = hasNihssAssessmentInput(data);
    } else if (isVcgScaleKey(scaleKey)) {
      hasValidData = hasVcgAssessmentInput(data);
    } else if (isMasScaleKey(scaleKey)) {
      hasValidData = hasMasAssessmentInput(data);
    } else if (isRomScaleKey(scaleKey)) {
      hasValidData = hasRomAssessmentInput(data);
    } else if (isCtScaleKey(scaleKey)) {
      hasValidData = hasCtAssessmentInput(data);
    } else if (isFgtScaleKey(scaleKey)) {
      hasValidData = hasFgtAssessmentInput(data);
    } else if (isTcmsScaleKey(scaleKey)) {
      hasValidData = hasTcmsAssessmentInput(data);
    } else if (isBarthelScaleKey(scaleKey)) {
      hasValidData = hasBarthelAssessmentInput(data);
    } else if (isRpeScaleKey(scaleKey)) {
      hasValidData = hasRpeAssessmentInput(data);
    } else if (isAsiaScaleKey(scaleKey)) {
      hasValidData = hasAsiaAssessmentInput(data);
    } else {
      hasValidData =
        data &&
        Object.entries(data).some(
          ([key, value]) =>
            key !== "id" &&
            value !== null &&
            value !== undefined &&
            value !== "" &&
            !(typeof value === "object" && Object.keys(value).length === 0)
        );
    }

    if (!hasValidData) {
      message.error(
        isMocaScaleKey(scaleKey)
          ? "Please complete at least one MOCA item  before saving."
          : isMMT
            ? "Please select at least one MMT limb grade  before saving."
          : isGcsScaleKey(scaleKey)
            ? "Please select at least one GCS response before saving."
            : isRassScaleKey(scaleKey)
              ? "Please select a RASS score before saving."
              : isNihssScaleKey(scaleKey)
                ? "Please complete at least one NIHSS item  before saving."
          : isVcgScaleKey(scaleKey)
            ? "Please select at least one limb grade before saving."
                  : isMasScaleKey(scaleKey)
                    ? "Please select at least one MAS limb grade before saving."
                    : isRomScaleKey(scaleKey)
                      ? "Please enter at least one ROM value before saving."
                      : isCtScaleKey(scaleKey)
                        ? "Please select at least one CT item before saving."
                        : isFgtScaleKey(scaleKey)
                          ? "Please enter at least one grip value before saving."
                          : isTcmsScaleKey(scaleKey)
                            ? "Please complete at least one TCMS item before saving."
                            : isBarthelScaleKey(scaleKey)
                              ? "Please complete at least one Barthel Index item before saving."
                              : isRpeScaleKey(scaleKey)
                                ? "Please select an RPE value before saving."
            : isAsiaScaleKey(scaleKey)
              ? "Please enter ASIA assessment data  before saving."
              : "Please enter at least one value before saving the assessment."
      );
      return false;
    }

    setScaleLoading(true);
    try {
      let model = scaleKey;
      if (typeof scaleKey === "object") {
        model = scaleKey?.name || scaleKey?.key;
      }
      if (typeof model === "string") {
        const normalizedModel = model.trim();
        model =
          frontendToModelMap[normalizedModel] ||
          frontendToModelMap[normalizedModel.toLowerCase()] ||
          normalizedModel;
      }
      if (!model) {
        message.error("Unable to save scale: invalid model.");
        return false;
      }

      const payload = {
        ...data,
        op_admission: admissionId,
      };
      const editId = data?.id || editScaleData?.id;

      if (editId) {
        await updateOpScaleAPI(model, editId, payload);
        message.success(`${model} updated successfully`);
        setReturnToTable(true);
      } else {
        const res = await createOpScaleAPI(model, payload);
        setScalesId(res?.data?.id);
        window.location.hash = res?.data?.id;
        message.success(`${model} created successfully`);
      }

      setIsStartScalesVisible(false);
      setIsScaleDirty(false);

      await getScalesData();

      const res = await getOpProgressScalesDataAPI(admissionId, model);
      setSelectedScale(model);
      const dataKey = modelToFrontendKeyMap[model];
      const refreshed = (res.data.data || [])
        .map((item) => ({
          id: item.id,
          created_at: item.date,
          type: item.type,
          data: item?.[dataKey] ?? item?.data ?? item,
        }))
        .sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      setSelectedScaleData(refreshed);
      setIsModalVisible(true);
      return true;
    } catch (err) {
      console.error(err);
      return false;
    } finally {
      setScaleLoading(false);
    }
  };

  useEffect(() => {
    if (admissionId) getScalesData();
  }, [admissionId]);

  const orderedScalesData = useMemo(() => {
    if (!scalesData?.length) return [];
    return [...scalesData].sort((a, b) => {
      const ia = opProgressScalesDisplayOrder.indexOf(a.name);
      const ib = opProgressScalesDisplayOrder.indexOf(b.name);
      const ra = ia === -1 ? Number.MAX_SAFE_INTEGER : ia;
      const rb = ib === -1 ? Number.MAX_SAFE_INTEGER : ib;
      if (ra !== rb) return ra - rb;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [scalesData]);

  return (
    <>
      {isModalVisible && (
        <ScaleModalOp
          scaleKey={selectedScale === "CT" ? "CoordinationTest" : selectedScale}
          isVisible={isModalVisible}
          onClose={handleCloseModal}
          data={selectedScaleData}
          patientData={patientData}
          onAddScale={handleAddOrEditScale}
          returnToTable={returnToTable}
          onViewModeConsumed={() => setReturnToTable(false)}
        />
      )}
      <ScalePopup
        open={isScalePopupOpen}
        onClose={requestCloseScalePopup}
        size={
          activeScaleKey === "PeakFlowMeterTest" ||
          activeScaleKey === "FingerGripTest"
            ? "small"
            : "large"
        }
      >
        <PhysioScalesOp
          ref={scaleRef}
          admission={admissionId}
          patientData={patientData}
          onScalesSubmit={handleScalesSubmit}
          scalesId={scalesId}
          setScalesId={setScalesId}
          activeScaleKey={activeScaleKey}
          editData={editScaleData}
          op={true}
          notifyOnSave={true}
          onScaleDirty={() => setIsScaleDirty(true)}
          // Child scale components may already show their own confirm modals.
          // When they decide to close, we close immediately to avoid double modals.
          onClose={forceCloseScalePopup}
        />
      </ScalePopup>
      <UiSectionCard
        title="Progress Scales"
        className="progress-scales-surface"
        bodyClassName="progress-scales-body"
      >
        {loading ? (
          <LoadingIndicator message="Loading OP scales data..." />
        ) : (
          <Row
            gutter={[16, 16]}
            justify="space-between"
            style={{ padding: "0 20px" }}
          >
            {orderedScalesData?.map((item, index) => (
              <Col
                key={index}
                xs={12}
                sm={12}
                md={12}
                lg={6}
                xl={6}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Space className="progress-scale-card">
                  <UiButton
                    className="progress-scale-btn"
                    variant="ghost"
                    size="md"
                    onClick={() => handleBtnClick(item?.displayKey)}
                  >
                    {scaleLabels[item?.name]}
                  </UiButton>
                  <p className="progress-scale-count">{`Count: ${
                    item?.count || 0
                  }`}</p>
                </Space>
              </Col>
            ))}
          </Row>
        )}
      </UiSectionCard>
      {scaleLoading && <LoadingIndicator message="Loading scale details..." />}
    </>
  );
};

export default ProgressScalesOp;
